// pages/investor/StripeConnect.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Typography, Button, Spin, Alert, Space, Result, Divider } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, DollarOutlined, SafetyOutlined } from '@ant-design/icons';

import StripeOnboarding from './StripeOnboarding';
import { DataService } from '../../config/dataService/smipDataService';
import { handleApiError } from '../../utility/errorHandler';
import { PageHeader } from '../page-headers/page-headers';
import { Main } from '../../container/styled';

const { Title, Text, Paragraph } = Typography;

const PageRoutes = [
  {
    path: 'index',
    breadcrumbName: 'Dashboard',
  },
  {
    path: 'app',
    breadcrumbName: 'Investor',
  },
  {
    path: '',
    breadcrumbName: 'Connect Stripe',
  },
];

function InvestorStripeConnect() {
  const [investor, setInvestor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { investorId } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInvestorDetails = async () => {
      if (!investorId) {
        setError('Investor ID is not available');
        setLoading(false);
        return;
      }

      try {
        const response = await DataService.get(`/Investors?filter[id][_eq]=${investorId}`);

        if (response.data && response.data.length > 0) {
          setInvestor(response.data[0]);
        } else {
          setError('Investor not found');
        }
      } catch (err) {
        handleApiError(err);
        setError('Failed to load investor details');
      } finally {
        setLoading(false);
      }
    };

    fetchInvestorDetails();
  }, [investorId]);

  const handleBackToDashboard = () => {
    navigate('/investor/dashboard');
  };

  if (loading) {
    return (
      <Main>
        <PageHeader title="Connect Stripe Account" routes={PageRoutes} />
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>Loading investor details...</Paragraph>
        </div>
      </Main>
    );
  }

  if (error) {
    return (
      <Main>
        <PageHeader title="Connect Stripe Account" routes={PageRoutes} />
        <Card>
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            action={
              <Button type="primary" onClick={handleBackToDashboard}>
                Back to Dashboard
              </Button>
            }
          />
        </Card>
      </Main>
    );
  }

  const isStripeConnected = investor?.stripe_account_id;

  return (
    <Main>
      <PageHeader
        title="Connect Stripe Account"
        routes={PageRoutes}
        onBack={handleBackToDashboard}
        backIcon={<ArrowLeftOutlined />}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} md={isStripeConnected ? 24 : 16} lg={isStripeConnected ? 16 : 12}>
          {isStripeConnected ? (
            <Card>
              <Result
                status="success"
                icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                title="Your Stripe account is connected!"
                subTitle={`Account ID: ${investor.stripe_account_id}`}
                extra={[
                  <Button
                    type="primary"
                    icon={<DollarOutlined />}
                    key="fund"
                    onClick={() => navigate(`/investor/${investorId}/fund`)}
                  >
                    Load Funds
                  </Button>,
                  <Button key="invest" onClick={() => navigate(`/investor/${investorId}/invest`)}>
                    Invest in Project
                  </Button>,
                ]}
              />
              <Divider />
              <Paragraph>Now that your Stripe account is connected, you can:</Paragraph>
              <ul>
                <li>
                  <Text>Load funds to your investment account</Text>
                </li>
                <li>
                  <Text>Invest in available projects</Text>
                </li>
                <li>
                  <Text>Track your investment portfolio</Text>
                </li>
              </ul>
            </Card>
          ) : (
            <Card>
              <Title level={4}>Connect Your Stripe Account</Title>
              <Paragraph>
                To invest in projects, you need to connect your account to Stripe. This allows us to process your
                investments securely.
              </Paragraph>

              <StripeOnboarding
                userType="investor"
                userId={investorId}
                email={investor?.user_id?.email || investor?.email}
              />
            </Card>
          )}
        </Col>

        {isStripeConnected ? (
          <Col xs={24} lg={8}>
            <Card>
              <Space direction="vertical" size="middle">
                <div>
                  <Title level={4}>Account Status</Title>
                  <Alert
                    message="Your account is ready"
                    description="Your Stripe account is fully connected and ready to use."
                    type="success"
                    showIcon
                    icon={<SafetyOutlined />}
                  />
                </div>

                <div>
                  <Button type="link" onClick={handleBackToDashboard}>
                    Return to Dashboard
                  </Button>
                </div>
              </Space>
            </Card>
          </Col>
        ) : (
          <Col xs={24} md={8} lg={12}>
            <Card>
              <Title level={4}>Why Connect Stripe?</Title>
              <Space direction="vertical" size="middle">
                <div>
                  <Text strong>Secure Investments</Text>
                  <Paragraph>
                    Stripe is a trusted payment processor that ensures your investments are handled securely.
                  </Paragraph>
                </div>

                <div>
                  <Text strong>Easy Fund Management</Text>
                  <Paragraph>Easily load funds to your investment account and track your portfolio.</Paragraph>
                </div>

                <div>
                  <Text strong>Quick Returns</Text>
                  <Paragraph>
                    When your investments yield returns, they&apos;ll be automatically credited to your account.
                  </Paragraph>
                </div>
              </Space>
            </Card>
          </Col>
        )}
      </Row>
    </Main>
  );
}

export default InvestorStripeConnect;
